import React from "react";
import { Tag } from "antd";
import { IncreaseIcon, DecreaseIcon, EqualIcon } from "./trendIcons.jsx";
import { splitAiColumnSelection } from "../aiColumnPresence";

const colorArray = [
  { dotcolor: "#1E3A8A", tabcolor: "#D6DBF1" },
  { dotcolor: "#065F46", tabcolor: "#CDE6DF" },
  { dotcolor: "#9A3412", tabcolor: "#F2D6CC" },
  { dotcolor: "#6B21A8", tabcolor: "#E0CFEF" },
  { dotcolor: "#B91C1C", tabcolor: "#F2C8C8" },
  { dotcolor: "#0F766E", tabcolor: "#CDE9E6" },
  { dotcolor: "#92400E", tabcolor: "#EDD8C2" },
  { dotcolor: "#7C3AED", tabcolor: "#E4D8FA" },
  { dotcolor: "#C2410C", tabcolor: "#F3D9C7" },
  { dotcolor: "#15803D", tabcolor: "#C6E5C9" },
  { dotcolor: "#1E40AF", tabcolor: "#D1DDF6" },
  { dotcolor: "#A21CAF", tabcolor: "#EFCDED" },
  { dotcolor: "#D97706", tabcolor: "#F7E2C4" },
  { dotcolor: "#047857", tabcolor: "#C6E9DE" },
  { dotcolor: "#B45309", tabcolor: "#EDD7BC" },
  { dotcolor: "#4338CA", tabcolor: "#D7D4F5" },
  { dotcolor: "#BE185D", tabcolor: "#F2C6D8" },
  { dotcolor: "#0369A1", tabcolor: "#CBE5F5" },
  { dotcolor: "#525252", tabcolor: "#E4E4E4" },
  { dotcolor: "#111827", tabcolor: "#D1D5DB" },
];

export const getValueTag = (value, score, type) => {
  // Handle null or undefined values
  if (score === null || score === undefined) {
    return (
      <Tag
        style={{
          backgroundColor: "#F4F4F5",
          borderColor: "#D4D4D8",
          color: "#71717A",
          borderRadius: "62.4375rem",
          padding: "0.0625rem 0.75rem",
          textAlign: "center",
        }}
      >
        -
      </Tag>
    );
  }

  let color = "";
  let backgroundColor = "";

  if (value) {
    if (value === "Yes") {
      color = "#16A34A";
      backgroundColor = "#E8FCEF";
    } else if (value === "No") {
      color = "#E71D36";
      backgroundColor = "#FAF2F3";
    }
  }

  if (score >= 90) {
    color = "#16A34A";
    backgroundColor = "#E8FCEF";
  } else if (score >= 60 && score < 90) {
    color = "#F79009";
    backgroundColor = "#FFFCF5";
  } else if (score < 60) {
    color = "#E71D36";
    backgroundColor = "#FAF2F3";
  }

  return (
    <Tag
      style={{
        backgroundColor: backgroundColor,
        borderColor: color,
        color: color,
        borderRadius: "62.4375rem",
        padding: "0.0625rem 0.75rem",
        textAlign: "center",
      }}
    >
      {value === "Yes" || value === "No"
        ? value
        : type === "score"
        ? value
        : `${score}%`}
    </Tag>
  );
};

export const getScoreChange = (value, type) => {
  const fontColor = value > 0 ? "#16A34A" : value === 0 ? "#27272A" : "#D03238";
  const bgColor = value > 0 ? "#F5FFF9" : value === 0 ? "#F4F4F5" : "#FCFAFA";
  const logo =
    value > 0 ? (
      <IncreaseIcon />
    ) : value === 0 ? (
      <EqualIcon />
    ) : (
      <DecreaseIcon />
    );

  const getContainerStyle = () => {
    if (type === "overview") {
      return "inline-flex px-[0.375rem] py-[0.1875rem] justify-center items-center rounded-xl gap-1.5 shadow-[1px_2px_6px_0px_rgba(0,0,0,0.10)]";
    }
    // Default style
    return "inline-flex px-2 py-0.5 justify-center items-center rounded-xl gap-1 border-[0.5px]";
  };

  if (value === null) {
    return (
      <div
        className={getContainerStyle()}
        style={{ backgroundColor: "#F4F4F5", borderColor: "#F4F4F5" }}
      >
        <div className="text-[0.625rem] text-[rgba(0,0,0,0.45)]">N/A</div>
      </div>
    );
  }

  return (
    <div
      className={getContainerStyle()}
      style={{ backgroundColor: bgColor, borderColor: bgColor }}
    >
      {logo}
      <div
        className={
          type === "overview" ? "text-[0.625rem] font-[500]" : "text-[0.625rem]"
        }
        style={{ color: fontColor }}
      >
        {Math.abs(value)}
      </div>
    </div>
  );
};

const colorPool = [...colorArray];

export const getRandomColor = () => {
  if (colorPool.length === 0) {
    colorPool.push(...colorArray);
  }

  const randomIndex = Math.floor(Math.random() * colorPool.length);
  const selectedColor = colorPool.splice(randomIndex, 1)[0];
  return selectedColor || { dotcolor: "#000000", tabcolor: "#FFFFFF" };
};

export const extractTitleText = (title, fallbackKey) => {
  const specialColumnNames = {
    score: "Score",
    score_change: "Score Change",
    platform: "Platform",
    franchise_name: "Franchise",
    available: "Availability",
    competitor_availability: "Competitor Availability",
    core_availability: "Core Availability %",
    chain_flag: "Part of Chain",
    place_id: "Place ID",
    is_place_id_available: "On Google? (Y/N)",
    id_client_crm: "CRM ID",
    is_crm_available: "In CRM? (Y/N)",
    in_fixed_combo: "Fixed Combo",
    in_combo: "Combo",
    in_option_combo: "Option Combo",
    name_accuracy: "Name Accuracy",
    image_accuracy: "Image Accuracy",
  };

  if (specialColumnNames[fallbackKey]) {
    return specialColumnNames[fallbackKey];
  }

  if (typeof title === "string") {
    return title;
  }

  if (title && typeof title === "object") {
    const attemptExtraction = (element) => {
      if (!element) return null;
      if (typeof element === "string") return element;
      if (Array.isArray(element))
        return element.map(attemptExtraction).filter(Boolean).join("");
      if (element.props) {
        if (element.props.title && typeof element.props.title === "string")
          return element.props.title; // Tooltip
        if (element.props.children)
          return attemptExtraction(element.props.children);
      }
      return null;
    };
    const extracted = attemptExtraction(title);
    if (extracted) return extracted;
  }

  return fallbackKey
    ? fallbackKey.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
    : "";
};

/**
 * Deep-checks whether a filter group contains any meaningful value.
 * restructureFilterOptions always initializes empty husks like sku_filters /
 * brand_metric_filters; the save path uses this to drop them so empty
 * objects never get persisted to Firestore.
 */
const hasFilterValues = (group) => {
  if (group === null || group === undefined) return false;
  if (Array.isArray(group)) return group.length > 0;
  if (typeof group === "object") {
    return Object.values(group).some(hasFilterValues);
  }
  return true;
};

/**
 * Removes filter groups that hold no meaningful values, returning an
 * applied_filters object fit for persistence.
 * @param {Object} filter - result.filter from restructureFilterOptions
 * @returns {Object} object containing only non-empty filter groups
 */
export const pruneEmptyFilterGroups = (filter) => {
  return Object.fromEntries(
    Object.entries(filter || {}).filter(([, group]) => hasFilterValues(group))
  );
};

export const restructureFilterOptions = (filterOptions, activeTabKey, id_client, skuParameters = null) => {
  if (!filterOptions || Object.keys(filterOptions).length === 0) {
    return {};
  }

  const result = {
    filter: {
      outlet_filters: {},
      metric_filters: {},
      // Filters exclusive to one SKU tab; reset when the SKU tab changes
      sku_filters: {},
      // Brand-specific metric filters for Red Bull MEWA
      brand_metric_filters: {
        brand_pct_name_accuracy: {},
        brand_pct_image_presence: {},
      },
    },
  };

  const outletFilterKeys = [
    "segment_type",
    "segment_nodes",
    "franchise",
    "rating",
    "price_level",
    "review",
    "geo_level_1",
    "geo_level_2",
    "territory_nodes",
    "postal_code",
    "place_id",
    "id_client_crm",
    "platform",
    "chain_flag",
    "part_of_chain",
    "cuisine",
    "city",
  ];

  // Metric field mappings for the global (non-SKU-tab) view
  const fieldMappings = {
    // part_of_chain: "chain_flag",
    chain_flag: "part_of_chain",
    available: "is_available",
    competitor_availability: "is_competitor_available",
    is_place_id_available: "is_place_id_available",
    is_crm_available: "is_crm_available",
    review: "review_nr",
    core_availability: "pct_core_availability",
    in_combo: "pct_combo",
    in_fixed_combo: "pct_combo_fixed",
    in_option_combo: "pct_combo_option",
    name_accuracy: "pct_name_accuracy",
    image_accuracy: "pct_image_availability", // Map image_accuracy to pct_image_availability
    image_presence: "pct_image_availability",
  };

  const isSkuTab = activeTabKey && activeTabKey !== "PET_Performance";
  // Specific SKU = subbrand + at least one of packaging_type/packaging_size/volume
  const isSpecificSku = isSkuTab && skuParameters?.subbrand && (
    skuParameters?.packaging_type || skuParameters?.packaging_size || skuParameters?.volume
  );

  // SKU-tab-only mappings onto the backend parameter names
  const skuFieldMappings = {
    available: "is_available_sku",
    competitor_availability: "competitor_availability_sku",
    in_combo: "is_combo_sku",
    in_fixed_combo: "is_fixed_combo_sku",
    in_option_combo: "is_option_combo_sku",
    name_accuracy: "sku_name_accuracy", // range {min, max}
    image_presence: "with_sku_image", // boolean
    image_accuracy: "with_sku_image", // boolean (alias for image_presence)
    // Specific SKU: boolean filter (sku_correct_image); subbrand-only: range filter (sku_pct_correct_image)
    pct_correct_image: isSpecificSku ? "sku_correct_image" : "sku_pct_correct_image",
  };
  const skuFilterKeys = new Set([
    "available",
    "competitor_availability",
    "in_combo",
    "in_fixed_combo",
    "in_option_combo",
    "name_accuracy",
    "image_accuracy",
  ]);

  Object.entries(filterOptions).forEach(([key, value]) => {
    if (value === null || value === undefined) {
      return;
    }

    if (Array.isArray(value) && value.length === 0) {
      return;
    }

    // AI Column filter — route flat `custom_<field_name>` keys into
    // custom_field_filters keyed by `<field_name>`: the reserved "Has value" /
    // "No value" options into `presence`, the rest into `values`. Without this
    // branch, the catch-all below would dump them into metric_filters where
    // they're ignored by the backend.
    if (key.startsWith("custom_") && Array.isArray(value) && value.length > 0) {
      const fieldName = key.replace(/^custom_/, "");
      const selection = splitAiColumnSelection(value);
      if (!selection) return;
      if (!result.filter.custom_field_filters) {
        result.filter.custom_field_filters = { values: {} };
      }
      const customFieldFilters = result.filter.custom_field_filters;
      if (selection.values.length > 0) {
        customFieldFilters.values[fieldName] = selection.values;
      }
      if (selection.presence) {
        customFieldFilters.presence = {
          ...customFieldFilters.presence,
          [fieldName]: selection.presence,
        };
      }
      return;
    }

    // Handle brand-specific filters for Red Bull MEWA
    if ((key.endsWith('_name_accuracy') && key !== 'name_accuracy') || 
        (key.endsWith('_image_presence') && key !== 'image_presence')) {
      
      if (key.endsWith('_name_accuracy')) {
        // Extract brand name
        const brandName = key.replace(/_name_accuracy$/, '');
        
        // Support two formats: [{from: 0, to: 100}] or [0, 100]
        if (Array.isArray(value) && value.length > 0) {
          if (value[0]?.from !== undefined && value[0]?.to !== undefined) {
            // Format: [{from: 0, to: 100}]
            result.filter.brand_metric_filters.brand_pct_name_accuracy[brandName] = {
              min: value[0].from,
              max: value[0].to
            };
          } else if (value.length === 2 && typeof value[0] === 'number' && typeof value[1] === 'number') {
            // Format: [0, 100]
            result.filter.brand_metric_filters.brand_pct_name_accuracy[brandName] = {
              min: value[0],
              max: value[1]
            };
          }
        }
      } else if (key.endsWith('_image_presence')) {
        // Extract brand name
        const brandName = key.replace(/_image_presence$/, '');
        // Value is an array like [true] or [false]
        if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'boolean') {
          result.filter.brand_metric_filters.brand_pct_image_presence[brandName] = value[0];
        }
      }
      return;
    }

    const actualKey = isSkuTab
      ? skuFieldMappings[key] || fieldMappings[key] || key
      : fieldMappings[key] || key;

    if (outletFilterKeys.includes(key)) {
      if (key === "price_level") {
        // The backend takes numeric price levels; "$$$$+" collapses onto 5

        const processedPriceLevel = value
          .map((item) => {
            if (item === "$") return 1;
            if (item === "$$") return 2;
            if (item === "$$$") return 3;
            if (item === "$$$$") return 4;
            if (item === "$$$$+") return 5;
            else return item;
          })
          .flat();
        result.filter.outlet_filters[actualKey] = processedPriceLevel;
      } else if (key === "chain_flag" || key === "part_of_chain") {
        if (Array.isArray(value) && value.length > 0) {
          result.filter.outlet_filters[actualKey] = value[0] ? 1 : 0;
        }
      } else if (key === "rating" || key === "review") {
        // Range type; accepts a [min, max] array
        if (
          Array.isArray(value) &&
          value.length === 2 &&
          value.every((v) => typeof v === "number")
        ) {
          result.filter.outlet_filters[actualKey] = [value[0], value[1]];
        } else if (
          Array.isArray(value) &&
          value[0]?.from !== undefined &&
          value[0]?.to !== undefined
        ) {
          // Legacy [{ from, to }] shape
          result.filter.outlet_filters[actualKey] = [
            value[0].from,
            value[0].to,
          ];
        }
      } else {
        result.filter.outlet_filters[actualKey] = value;
      }
    } else {
      if (key === "is_place_id_available") {
        const boolVal = Array.isArray(value) ? value[0] : value;
        result.filter.outlet_filters[actualKey] = boolVal ? 1 : 0;
        return;
      }
      if (key === "place_id") {
        result.filter.outlet_filters[actualKey] = value;
        return;
      }
      if (key === "is_crm_available") {
        const boolVal = Array.isArray(value) ? value[0] : value;
        result.filter.outlet_filters[actualKey] = boolVal ? 1 : 0;
        return;
      }
      if (key === "id_client_crm") {
        result.filter.outlet_filters[actualKey] = value;
        return;
      }

      // The synthetic __pet_available key carries global availability, which
      // the backend reads as metric_filters.is_available
      if (key === "__pet_available") {
        if (Array.isArray(value) && value.length > 0) {
          result.filter.metric_filters.is_available = value[0] ? 1 : 0;
        } else if (typeof value === "boolean") {
          result.filter.metric_filters.is_available = value ? 1 : 0;
        }
        return;
      }
      if (isSkuTab && skuFieldMappings[key]) {
        // -------- SKU tab specific logic --------
        if (
          [
            "available",
            "competitor_availability",
            "in_combo",
            "in_fixed_combo",
            "in_option_combo",
          ].includes(key)
        ) {
          // Single-select boolean from a dropdown, so the value arrives as
          // [1] / [0] / [true] / [false]
          if (Array.isArray(value) && value.length > 0) {
            result.filter.sku_filters[actualKey] = !!value[0];
          } else if (typeof value === "boolean" || value === 0 || value === 1) {
            result.filter.sku_filters[actualKey] = !!value;
          }
        } else if (key === "name_accuracy") {
          // Array [min, max] for sku_name_accuracy
          if (
            Array.isArray(value) &&
            value.length === 2 &&
            value.every((v) => typeof v === "number")
          ) {
            result.filter.sku_filters[actualKey] = [value[0], value[1]];
          } else if (
            Array.isArray(value) &&
            value[0]?.from !== undefined &&
            value[0]?.to !== undefined
          ) {
            result.filter.sku_filters[actualKey] = [value[0].from, value[0].to];
          }
        } else if (key === "pct_correct_image") {
          if (isSpecificSku) {
            // Specific SKU: convert to boolean for sku_correct_image
            if (Array.isArray(value) && value.length === 2 && typeof value[0] === "number") {
              result.filter.sku_filters[actualKey] = value[1] > 0;
            } else if (Array.isArray(value) && value.length > 0) {
              result.filter.sku_filters[actualKey] = !!value[0];
            } else if (typeof value === "boolean" || value === 0 || value === 1) {
              result.filter.sku_filters[actualKey] = !!value;
            }
          } else {
            // Subbrand-only: send as range [min, max] for sku_pct_correct_image
            if (Array.isArray(value) && value.length === 2 && typeof value[0] === "number") {
              result.filter.sku_filters[actualKey] = [value[0], value[1]];
            } else if (
              Array.isArray(value) &&
              value[0]?.from !== undefined &&
              value[0]?.to !== undefined
            ) {
              result.filter.sku_filters[actualKey] = [value[0].from, value[0].to];
            }
          }
        } else if (key === "image_presence" || key === "image_accuracy") {
          // Boolean for with_sku_image
          if (Array.isArray(value) && value.length === 2 && typeof value[0] === "number") {
            result.filter.sku_filters[actualKey] = value[1] > 0;
          } else if (Array.isArray(value) && value.length > 0) {
            result.filter.sku_filters[actualKey] = !!value[0];
          } else if (typeof value === "boolean" || value === 0 || value === 1) {
            result.filter.sku_filters[actualKey] = !!value;
          }
        }
      } else if (key === "score") {
        if (
          Array.isArray(value) &&
          value.length === 2 &&
          value.every((v) => typeof v === "number")
        ) {
          result.filter.metric_filters[actualKey] = [value[0], value[1]];
        } else if (
          Array.isArray(value) &&
          value[0]?.from !== undefined &&
          value[0]?.to !== undefined
        ) {
          result.filter.metric_filters[actualKey] = [
            value[0].from,
            value[0].to,
          ];
        }
      } else if (key === "core_availability") {
        // Core availability range [min, max]
        if (
          Array.isArray(value) &&
          value.length === 2 &&
          value.every((v) => typeof v === "number")
        ) {
          result.filter.metric_filters[actualKey] = [value[0], value[1]];
        } else if (
          Array.isArray(value) &&
          value[0]?.from !== undefined &&
          value[0]?.to !== undefined
        ) {
          result.filter.metric_filters[actualKey] = [
            value[0].from,
            value[0].to,
          ];
        }
      } else if (!isSkuTab && (key === "name_accuracy" || key === "image_presence")) {
        // ONE Performance tab: range [min, max]
        if (
          Array.isArray(value) &&
          value.length === 2 &&
          value.every((v) => typeof v === "number")
        ) {
          result.filter.metric_filters[actualKey] = [value[0], value[1]];
        } else if (
          Array.isArray(value) &&
          value[0]?.from !== undefined &&
          value[0]?.to !== undefined
        ) {
          result.filter.metric_filters[actualKey] = [
            value[0].from,
            value[0].to,
          ];
        }
      } else if (key === "score_change") {
        // The backend expects numeric score-change codes
        const scoreChangeMap = {
          up: 1,
          consistent: 0,
          down: -1,
          null: null, // the string "null"
        };

        if (Array.isArray(value)) {
          const mappedValues = value
            .map((val) => {
              // The selection can carry either a real null or the string "null"
              if (val === null || val === "null") {
                return null;
              }
              return scoreChangeMap[val];
            })
            .filter((val) => val !== undefined);

          if (mappedValues.length > 0) {
            result.filter.metric_filters[actualKey] = mappedValues;
          }
        }
      } else if (key === "part_of_chain") {
        if (Array.isArray(value) && value.length > 0) {
          result.filter.metric_filters[actualKey] = value[0] ? 1 : 0;
        }
      } else if (key === "has_brands_in_menu") {
        if (Array.isArray(value) && value.length > 0) {
          result.filter.metric_filters[actualKey] = !!value[0];
        } else if (typeof value === "boolean") {
          result.filter.metric_filters[actualKey] = value;
        }
      } else if (
        key === "listing_brand_category" ||
        key === "not_listing_brand_category" ||
        key === "is_offering_brands" ||
        key === "not_offering_brands"
      ) {
        if (Array.isArray(value) && value.length > 0) {
          result.filter.metric_filters[actualKey] = value[0];
        } else if (typeof value === "string" || typeof value === "number") {
          result.filter.metric_filters[actualKey] = value;
        }
      } else if (!isSkuTab && (key === "available" || key === "competitor_availability")) {
        // ONE Performance tab: availability
        if (Array.isArray(value) && value.length > 0) {
          result.filter.metric_filters[actualKey] = !!value[0];
        }
      } else if (key === "is_out_of_stock") {
        // Out of Stock overall filter: true->1, false->0, "__null__"->-1 (IS NULL)
        if (Array.isArray(value) && value.length > 0) {
          const v = value[0];
          if (v === "__null__") {
            result.filter.metric_filters.is_out_of_stock = -1;
          } else {
            result.filter.metric_filters.is_out_of_stock = v ? 1 : 0;
          }
        }
      } else if (key === "is_out_of_stock_sku") {
        // SKU Out of Stock filter: true->1, false->0, "__null__"->-1
        if (Array.isArray(value) && value.length > 0) {
          const v = value[0];
          if (v === "__null__") {
            result.filter.sku_filters.is_out_of_stock_sku = -1;
          } else {
            result.filter.sku_filters.is_out_of_stock_sku = v ? 1 : 0;
          }
        }
      } else if (key.startsWith("core_brand_out_of_stock_")) {
        // Per core brand out-of-stock filter: true->1, false->0, "__null__"->-1
        if (!result.filter.metric_filters.core_brand_out_of_stock) {
          result.filter.metric_filters.core_brand_out_of_stock = {};
        }
        // Extract brand name: "core_brand_out_of_stock_{brandName}_drink{N}"
        const withoutPrefix = key.replace("core_brand_out_of_stock_", "");
        const brandName = withoutPrefix.replace(/_drink\d+$/, "");
        if (value !== undefined && value.length > 0) {
          const v = value[0];
          if (v === "__null__") {
            result.filter.metric_filters.core_brand_out_of_stock[brandName] = -1;
          } else {
            result.filter.metric_filters.core_brand_out_of_stock[brandName] =
              v ? 1 : 0;
          }
        }
      } else if (key.startsWith("core_brand_availability_")) {
        // Core brand availability, keyed per brand
        if (!result.filter.metric_filters.core_brand_availability) {
          result.filter.metric_filters.core_brand_availability = {};
        }
        const splitName = key.split("_");
        const brandName = splitName[splitName.length - 2];

        if (value !== undefined && value.length > 0) {
          result.filter.metric_filters.core_brand_availability[brandName] =
            value[0];
        }
      } else if (
        !isSkuTab &&
        (key === "in_combo" ||
        key === "in_fixed_combo" ||
        key === "in_option_combo")
      ) {
        // ONE Performance tab: combo metrics, as a range with a boolean fallback
        const target = result.filter.metric_filters;
        if (
          Array.isArray(value) &&
          value[0]?.from !== undefined &&
          value[0]?.to !== undefined
        ) {
          target[actualKey] = [value[0].from, value[0].to];
        } else if (Array.isArray(value) && value.length > 0) {
          // Combo metrics take a boolean, not a 1/0 flag
          target[actualKey] = !!value[0];
        } else if (typeof value === "boolean") {
          // Combo metrics take a boolean, not a 1/0 flag
          target[actualKey] = value;
        }
      } else if (
        !isSkuTab &&
        (key === "core_availability" ||
        key === "name_accuracy" ||
        key === "image_accuracy")
      ) {
        // ONE Performance tab: range metrics, as a [min, max] array
        if (
          Array.isArray(value) &&
          value.length === 2 &&
          value.every((v) => typeof v === "number")
        ) {
          result.filter.metric_filters[actualKey] = [value[0], value[1]];
        } else if (
          Array.isArray(value) &&
          value[0]?.from !== undefined &&
          value[0]?.to !== undefined
        ) {
          // Legacy [{ from, to }] shape
          const target = result.filter.metric_filters;
          target[actualKey] = [value[0].from, value[0].to];
        }
      } else {
        const target =
          isSkuTab && skuFilterKeys.has(key)
            ? result.filter.sku_filters
            : result.filter.metric_filters;
        if (
          (key === "available" || key === "competitor_availability") &&
          Array.isArray(value) &&
          value.length > 0
        ) {
          target[actualKey] = value[0] ? 1 : 0;
        } else {
          target[actualKey] = value;
        }
      }
    }
  });

  // Drop the empty groups so callers never send bare filter objects
  if (Object.keys(result.filter.outlet_filters).length === 0) {
    delete result.filter.outlet_filters;
  }

  if (Object.keys(result.filter.metric_filters).length === 0) {
    delete result.filter.metric_filters;
  }

  if (Object.keys(result.filter.sku_filters).length === 0) {
    delete result.filter.sku_filters;
  }

  // Clean up empty brand_metric_filters
  if (result.filter.brand_metric_filters) {
    const hasNameAccuracy = Object.keys(result.filter.brand_metric_filters.brand_pct_name_accuracy || {}).length > 0;
    const hasImagePresence = Object.keys(result.filter.brand_metric_filters.brand_pct_image_presence || {}).length > 0;
    
    if (!hasNameAccuracy) {
      delete result.filter.brand_metric_filters.brand_pct_name_accuracy;
    }
    if (!hasImagePresence) {
      delete result.filter.brand_metric_filters.brand_pct_image_presence;
    }
    
    // If both are empty, remove brand_metric_filters entirely
    if (!hasNameAccuracy && !hasImagePresence) {
      delete result.filter.brand_metric_filters;
    }
  }

  if (Object.keys(result.filter).length === 0) {
    delete result.filter;
  }

  return result;
};

// -------- Country helpers (dedup and reusable across pages) --------

// Normalize raw country code, trimming wrapping quotes and whitespace
export const normalizeCountryCode = (code) => {
  if (typeof code === "string") {
    const normalized = code.replace(/^"|"$/g, "").trim();
    // Return normalized string only if it's non-empty, otherwise return null
    return normalized || null;
  }
  return code;
};

// Extract a stable ordered list of valid country codes from auth.countries tree
export const getValidCountryCodes = (countriesTree) => {
  const codes = [];
  const seen = new Set();
  if (!Array.isArray(countriesTree)) return codes;
  for (const region of countriesTree) {
    const nodes = region?.countries?.length
      ? region.countries
      : region?.children?.length
      ? region.children
      : [];
    for (const entry of nodes) {
      // Normalize both fields first, then fallback if the first is invalid
      let code = normalizeCountryCode(entry?.country);
      if (!code) {
        code = normalizeCountryCode(entry?.country_code);
      }
      // Only add valid, non-null, non-empty country codes
      if (code && !seen.has(code)) {
        seen.add(code);
        codes.push(code);
      }
    }
  }
  return codes;
};

// Filter-dimension alerts. App.jsx fires
// initializeFilterDimensions(id_client, currentCountry) on first render. If the
// persisted Redux state holds an inconsistent pair — e.g. id_client=pepsi_fr with
// currentCountry=AT carried over from a previous workspace — the per-dimension
// endpoints get hit with a (client, country) the client doesn't cover. This
// helper gates the dispatch on the pair being consistent with state.auth.countries.
//
// The empty-countries case must gate, NOT permit. On a page reload
// redux-persist rehydrates a stale currentCountry (e.g. ko_global + IN, where IN
// belongs to neither ko_global's geoscope nor its data) *before* auth.countries
// has loaded. Returning true while the list is empty would let that stale pair leak
// a request through, so this returns false until countries is known. The App.jsx
// effect lists `countries` in its dep array, so it re-runs once the real list
// arrives and fires then if the pair is valid — init is merely deferred, never
// permanently blocked. (If
// countries never loads, the whole workspace context is broken and suppressing
// filter init is the correct failure mode, not a regression.)
export const isFilterContextConsistent = (currentCountry, countriesTree) => {
  const valid = getValidCountryCodes(countriesTree);
  if (valid.length === 0) return false;
  const normalized = normalizeCountryCode(currentCountry);
  return Boolean(normalized) && valid.includes(normalized);
};

// Gate for the HomePage initial data fetch (fetchOutlets +
// subbrand/drinks/companies). Returns true only when redux-persist has
// rehydrated, we have a client, and the persisted currentCountry is consistent
// with the resolved workspace countries. On a fresh load / workspace change,
// redux-persist restores a currentCountry from the *previous* workspace (e.g.
// ZA) before fetchClientCountries resolves the new workspace's list — firing
// the init fetch then sends a stale (id_client, country) the backend has no
// refresh data for, producing prod HTTP 500s. Because the consuming effect
// lists `countries` in its dep array, init is merely deferred (not blocked)
// until the country list loads and the reconciliation effect corrects
// currentCountry. Same gating idiom as App.jsx's filter-dimension init
// via isFilterContextConsistent.
export const shouldInitializeHomepageData = ({
  idClient,
  currentCountry,
  hasInitialized,
  isRehydrated,
  countries,
}) =>
  Boolean(idClient) &&
  Boolean(currentCountry) &&
  !hasInitialized &&
  Boolean(isRehydrated) &&
  isFilterContextConsistent(currentCountry, countries);

// Gate for the Physical Location / Physical Franchise dashboards' filter-dimension
// fetches (the non-lazy franchise-dimensions fetch, the lazy slice init, and the
// Physical Location context reset). Same stale-persisted-country race the HomePage
// and App.jsx gates cover, on a separate code path: these effects would otherwise
// fire on `id_client && country` alone, so on login / workspace switch they hit the
// per-dimension routes with a country rehydrated from the *previous* workspace before
// fetchClientCountries resolves the new list. Returns true only once we have a client
// and the persisted country is consistent with the resolved
// workspace countries; the consuming effects list `countries` in their dep arrays, so
// init is deferred (not blocked) until the list loads and currentCountry reconciles.
export const shouldFetchPhysicalFilterDimensions = ({
  idClient,
  country,
  countries,
}) =>
  Boolean(idClient) &&
  Boolean(country) &&
  isFilterContextConsistent(country, countries);

// Classify the terminal outcome of a workspace switch once the countries fetch
// has been given a chance to settle. Keeping this pure (separate from the polling
// loop in useHomePage) is what lets the UI distinguish "still loading" from
// "genuinely no countries": while the fetch is in flight (`loading` true) an empty
// list means "not loaded yet", NOT an empty workspace — reporting it as the latter
// is the false "No available countries" error this guards against. filtersSlice
// selects the country when the fetch settles, so a settled list either holds the
// selected country or has none to select. Outcomes:
//   { type: "hang" }                 fetch never settled — transient failure, retry
//   { type: "use-current", country } the selected country is valid for the new list
//   { type: "empty" }                settled with a genuinely empty country list
export const classifyWorkspaceCountriesOutcome = ({
  loading,
  countries,
  currentCountry,
}) => {
  if (loading) return { type: "hang" };
  // isFilterContextConsistent already returns false for an empty list.
  if (currentCountry && isFilterContextConsistent(currentCountry, countries || [])) {
    return { type: "use-current", country: currentCountry };
  }
  return { type: "empty" };
};
